/**
 * Centralized route generation for the partner portal.
 * All routes return DynamicRoute type to bypass Next.js strict RouteImpl type checking.
 */

/**
 * Helper to append query string to a route
 * Usage:
 *
 * const searchParams = useSearchParams();
 * const route = partnerRoutes.home(partnerId, searchParams);
 */
export function withQuery<T extends `/${string}`>(
	path: T,
	params?: URLSearchParams | null | string,
): T | `${T}?${string}` {
	if (!params) return path as T;

	const queryString = typeof params === 'string' ? params : params.toString();
	return (queryString ? `${path}?${queryString}` : path) as T | `${T}?${string}`;
}

/**
 * Helper to append hash to a route
 * Accepts paths with or without query strings
 */
function withHash<T extends `/${string}` | `/${string}?${string}`>(
	path: T,
	hash?: string,
): T | `${T}#${string}` {
	return (hash ? `${path}#${hash}` : path) as T | `${T}#${string}`;
}

/**
 * Global routes (no partnerId required)
 */
export const routes = {
	groups: (query?: URLSearchParams | null) => withQuery('/groups', query),
	create: (query?: URLSearchParams | null) => withQuery('/create', query),
	login: (query?: URLSearchParams | null) => withQuery('/login', query),
};

/**
 * Shorthand for routes.groups
 */
export const groupsRoute = routes.groups;

/**
 * Partner-scoped routes
 */
export const partnerRoutes = {
	home: (partnerId: string, query?: URLSearchParams | null) => withQuery(`/${partnerId}/home`, query),

	account: {
		index: (partnerId: string, query?: URLSearchParams | null, hash?: string) =>
			withHash(withQuery(`/${partnerId}/account`, query), hash),
		details: (partnerId: string): `/${string}/account/details` => `/${partnerId}/account/details`,
		sso: (partnerId: string, query?: URLSearchParams | null) => withQuery(`/${partnerId}/account/sso`, query),
		plan: (partnerId: string, query?: URLSearchParams | null) =>
			withQuery(`/${partnerId}/account/plan`, query),
		rolesAndPermissions: (partnerId: string): `/${string}/account/roles-and-permissions` =>
			`/${partnerId}/account/roles-and-permissions`,
		admin: (partnerId: string): `/${string}/account/admin` => `/${partnerId}/account/admin`,
	},

	reporting: {
		index: (partnerId: string): `/${string}/reporting` => `/${partnerId}/reporting`,
		dashboard: (partnerId: string, query?: URLSearchParams | null) =>
			withQuery(`/${partnerId}/reporting/dashboard`, query),
		feedback: (partnerId: string): `/${string}/reporting/feedback` => `/${partnerId}/reporting/feedback`,
		dataConfig: (partnerId: string): `/${string}/reporting/data-config` =>
			`/${partnerId}/reporting/data-config`,
		previewReporting: (partnerId: string): `/${string}/reporting/preview-reporting` =>
			`/${partnerId}/reporting/preview-reporting`,
	},

	eligibility: {
		users: (partnerId: string, query?: URLSearchParams | null, hash?: string) =>
			withHash(withQuery(`/${partnerId}/eligibility/users`, query), hash),
		accessCodes: (partnerId: string): `/${string}/eligibility/access-codes` =>
			`/${partnerId}/eligibility/access-codes`,
		criteria: (partnerId: string): `/${string}/eligibility/criteria` => `/${partnerId}/eligibility/criteria`,
		filesAndLogs: (partnerId: string): `/${string}/eligibility/files-and-logs` =>
			`/${partnerId}/eligibility/files-and-logs`,
		integration: (partnerId: string): `/${string}/eligibility/integration` =>
			`/${partnerId}/eligibility/integration`,
	},

	config: {
		aiFeatures: (partnerId: string): `/${string}/config/ai-features` => `/${partnerId}/config/ai-features`,
		branding: (partnerId: string): `/${string}/config/branding` => `/${partnerId}/config/branding`,
		emailAndSurveys: (partnerId: string): `/${string}/config/email-and-surveys` =>
			`/${partnerId}/config/email-and-surveys`,
		setup: (partnerId: string): `/${string}/config/setup` => `/${partnerId}/config/setup`,
	},

	marketing: {
		resources: (partnerId: string, query?: URLSearchParams | null) =>
			withQuery(`/${partnerId}/marketing/resources`, query),
		resourcesHub: (partnerId: string): `/${string}/marketing/resources-hub` =>
			`/${partnerId}/marketing/resources-hub`,
	},

	pathways: {
		index: (partnerId: string): `/${string}/pathways` => `/${partnerId}/pathways`,
		create: (partnerId: string, hash?: string) => withHash(`/${partnerId}/pathways/create`, hash),
		edit: (partnerId: string, pathwayId: string, hash?: string) =>
			withHash(`/${partnerId}/pathways/edit/${pathwayId}`, hash),
	},

	plan: (partnerId: string, query?: URLSearchParams | null) => withQuery(`/${partnerId}/plan`, query),

	benefits: {
		sponsorGroups: (partnerId: string): `/${string}/benefits/sponsor-groups` =>
			`/${partnerId}/benefits/sponsor-groups`,
		restOfWorld: (partnerId: string): `/${string}/benefits/rest-of-world` =>
			`/${partnerId}/benefits/rest-of-world`,
	},

	sponsorGroups: (partnerId: string): `/${string}/sponsor-groups` => `/${partnerId}/sponsor-groups`,

	// Legacy routes (kept for backwards compatibility)
	users: (partnerId: string, query?: URLSearchParams | null, hash?: string) =>
		withHash(withQuery(`/${partnerId}/users`, query), hash),

	resources: (partnerId: string, query?: URLSearchParams | null) =>
		withQuery(`/${partnerId}/resources`, query),
};
